import type { App, DefineComponent, Component, ComponentPublicInstance, Plugin } from "vue";
import type { EConnectElementOptions } from "./eConnectElementOptions";

import { defineCustomElement, defineSSRCustomElement } from "vue";
import { addGlobalStylesToShadowRoot } from "./globalStyles";
import { useLogger } from "composables/useLogger";
import { globalPinia } from "./globalPinia";
import { withFormSubmitter } from "./withFormSubmitter";
import { SlotWhitespaceTrimmer } from "./whitespaceTrimming";
import { camelToKebab } from "./utils";
import { AttributeHandler } from "./attributeHandler";
import { storeElementInternals } from "composables/useFormAssociated";
import { useStateStore } from "composables/useStateStore";

type VueComponent = DefineComponent<any, any, any, any, any, any, any, any, any, any> | Component<any, any, any>;

const { logger } = useLogger("defineEConnectElement", { level: "debug" });

/** Configure a Vue app with Pinia and error handling. */
const configApp = (app: App): void => {
  app.use(globalPinia as Plugin);
  app.config.errorHandler = (err: unknown, _: ComponentPublicInstance | null, info: string) => {
    logger.error(info, err);
  };
};

/** Create the Vue custom element constructor from a component and options. */
const createVueCE = (component: VueComponent, elementOptions?: EConnectElementOptions): CustomElementConstructor => {
  const customOptions = elementOptions?.customElementOptions ?? { shadowRoot: true };
  const element = elementOptions?.ssr
    ? defineSSRCustomElement(component, { configureApp: configApp, ...customOptions })
    : defineCustomElement(component, { configureApp: configApp, ...customOptions });

  if (elementOptions?.formAssociated) {
    (element as any).formAssociated = true;
  }
  return element;
};

/** Initialize whitespace trimmer on shadow root. */
const initWhitespaceTrimmer = (host: HTMLElement, shadowRoot: ShadowRoot): SlotWhitespaceTrimmer => {
  const trimmer = new SlotWhitespaceTrimmer(host, shadowRoot);
  if ("requestIdleCallback" in globalThis) {
    requestIdleCallback(() => trimmer.init(), { timeout: 100 });
  } else {
    queueMicrotask(() => trimmer.init());
  }
  return trimmer;
};

/** Inject global styles into shadow root (once). */
const injectGlobalStyles = (shadowRoot: ShadowRoot): void => {
  if ((shadowRoot as any).__styledOnce) return;
  addGlobalStylesToShadowRoot(shadowRoot);
  (shadowRoot as any).__styledOnce = true;
};

/** Schedule deferred component state cleanup. */
const scheduleStateCleanup = (instance: any): void => {
  const uid = instance._instance?.uid;
  if (!uid) return;
  queueMicrotask(() => {
    if (!instance.isConnected) {
      try {
        const stateStore = useStateStore();
        stateStore.removeState(uid);
      } catch (error) {
        logger.error(`Failed to cleanup state for uid: ${uid}`, error);
      }
    }
  });
};

/** Forward attribute changes to Vue's base CE class. */
const forwardToSuperAttributeChanged = (
  instance: any,
  protoRef: any,
  name: string,
  oldValue?: string,
  newValue?: string,
): void => {
  const superCb = protoRef?.attributeChangedCallback;
  if (typeof superCb === "function") {
    try {
      superCb.call(instance, name, oldValue, newValue);
    } catch {
      /* Silently ignore errors from base class */
    }
  }
};

/**
 * Defines a custom element (web component) from a Vue component.
 */
export const defineEConnectElement = (
  component: VueComponent,
  elementOptions?: EConnectElementOptions,
): CustomElementConstructor => {
  const vueCE = createVueCE(component, elementOptions);
  const componentProps = component?.props;
  const attributes = componentProps ? Object.keys(componentProps) : [];
  const hasModelValueProp = !!componentProps && "modelValue" in componentProps;
  const opts = elementOptions ?? {};
  const base = opts.formSubmitter ? withFormSubmitter(vueCE) : vueCE;

  return buildEConnectBase({
    base,
    attributes,
    hasModelValueProp,
    dataAttrNames: attributes.map((attr) => `data-${camelToKebab(attr)}`),
    kebabAttrNames: attributes.map((attr) => camelToKebab(attr)),
    defaultTrim: opts.trimDefaultSlotWhitespace ?? true,
    attachInternalsOnConstruct: !!opts.formAssociated && !opts.formSubmitter,
  });
};

/** Compute the observed attributes array for the CE class. */
const getObservedAttrs = (
  attributes: string[],
  kebabAttrNames: string[],
  dataAttrNames: string[],
  hasModelValueProp: boolean,
): string[] => {
  const extra = hasModelValueProp ? ["value", "model-value"] : [];
  return [...new Set([...attributes, ...kebabAttrNames, ...extra, ...dataAttrNames])];
};

/** Resolve whether default slot whitespace trimming is enabled for an element. */
const shouldTrimDefaultSlot = (el: HTMLElement, defaultTrimValue: boolean): boolean => {
  const attr = el.dataset.trimDefaultSlot;
  if (attr != null) return attr !== "false";
  return defaultTrimValue;
};

/** Try to attach ElementInternals; silently ignore if not supported. */
const tryAttachInternals = (el: HTMLElement): void => {
  try {
    storeElementInternals(el, (el as any).attachInternals());
  } catch {
    /* not supported */
  }
};

/** Handle attribute change: data attrs, model value, and forward to super. */
const handleAttrChange = (
  instance: any,
  protoRef: any,
  handler: AttributeHandler,
  hasModelValue: boolean,
  name: string,
  oldValue?: string,
  newValue?: string,
): void => {
  if (oldValue === newValue) return;
  handler.handleDataAttributeChange(name, newValue);
  handler.handleModelValueChange(name, newValue);
  if (!(hasModelValue && name === "value")) {
    forwardToSuperAttributeChanged(instance, protoRef, name, oldValue, newValue);
  }
};

interface EConnectBaseConfig {
  base: CustomElementConstructor;
  attributes: string[];
  hasModelValueProp: boolean;
  dataAttrNames: string[];
  kebabAttrNames: string[];
  defaultTrim: boolean;
  attachInternalsOnConstruct: boolean;
}

/** Build the enhanced EConnectBase class. */
const buildEConnectBase = (cfg: EConnectBaseConfig): CustomElementConstructor => {
  const { base, attributes, hasModelValueProp, defaultTrim, attachInternalsOnConstruct } = cfg;
  const observedAttrs = getObservedAttrs(attributes, cfg.kebabAttrNames, cfg.dataAttrNames, hasModelValueProp);
  class EConnectBase extends base {
    [key: string]: any;
    readonly #attributeHandler: AttributeHandler;
    #whitespaceTrimmer?: SlotWhitespaceTrimmer;
    #isDestroyed = false;
    #didParseDataset = false;
    #didInitTrim = false;
    constructor() {
      super();
      this.#attributeHandler = new AttributeHandler(this, attributes, hasModelValueProp);
      if (attachInternalsOnConstruct) tryAttachInternals(this);
    }
    static get observedAttributes(): string[] {
      return observedAttrs;
    }
    static readonly trimDefaultSlotWhitespace = defaultTrim;
    static readonly #proto = Object.getPrototypeOf(EConnectBase.prototype);
    connectedCallback(): void {
      this.#isDestroyed = false;
      if (!this.#didParseDataset) {
        this.#attributeHandler.parseDatasetAttributes();
        this.#didParseDataset = true;
      }
      if (hasModelValueProp) this.#attributeHandler.normalizeModelValue();
      // @ts-expect-error CE lifecycle not typed on HTMLElement
      super.connectedCallback();
      if (!this.#didInitTrim && shouldTrimDefaultSlot(this, defaultTrim) && this.shadowRoot) {
        this.#whitespaceTrimmer = initWhitespaceTrimmer(this, this.shadowRoot);
        this.#didInitTrim = true;
      }
      if (this.shadowRoot) injectGlobalStyles(this.shadowRoot);
    }
    disconnectedCallback(): void {
      if (this.#isDestroyed) return;
      this.#isDestroyed = true;
      this.#whitespaceTrimmer?.destroy();
      scheduleStateCleanup(this);
      // @ts-expect-error CE lifecycle not typed on HTMLElement
      super.disconnectedCallback();
    }
    attributeChangedCallback(name: string, oldValue?: string, newValue?: string): void {
      handleAttrChange(this, EConnectBase.#proto, this.#attributeHandler, hasModelValueProp, name, oldValue, newValue);
    }
  }
  return EConnectBase as unknown as CustomElementConstructor;
};
